/**
 * Which sim events become a chat line, and what the line says.
 *
 * The chat log is the party's communication surface. It carries what a party
 * member needs to know about the floor — who found the key, who is down, who
 * teleported, who is gone — and nothing else. Two rules keep it readable:
 *
 * - **Combat never chats.** Every swing already draws its own hitsplat over
 *   its target (effects.ts: "chat is for the log, the splat is the at-a-glance
 *   feedback"), and a kill leaves its bones decal. A measured large floor
 *   produced 16 log lines a minute at 94% combat noise, which buried the
 *   party's own messages. A hero going down is the one combat fact the party
 *   cannot read off the floor, so it stays.
 * - **Personal actions never chat.** Equipping, unequipping and reordering are
 *   visible in your own rack; a rejected click is feedback for one player, so
 *   it renders as a transient line (`kind: "feedback"`), not a log entry.
 *
 * Copy style (docs/game_mechanics.md): sentence case, one sentence, terminal
 * period, no em dashes.
 *
 * A pure function over the wire event, so the rule is testable without a DOM
 * (packages/client/test/event-lines.test.ts).
 */
export type ChatLineKind = "party" | "system" | "feedback";

export interface ChatLine {
  kind: ChatLineKind;
  /** The speaker for a party line (chat only). */
  name?: string;
  text: string;
}

export interface LineNames {
  /** Display name for a player id. */
  player(id: number): string;
  /** True for this client's own player id. */
  isMe(id: number): boolean;
  /** Display name for a dropped item, or null when the catalog has none. */
  item(cls: unknown, kind: number): string | null;
}

const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
const num = (v: unknown): number | null => (typeof v === "number" ? v : null);

/** The chat line an event produces, or null when it stays out of the log. */
export function chatLineFor(raw: unknown, names: LineNames): ChatLine | null {
  if (typeof raw !== "object" || raw === null) return null;
  const e = raw as Record<string, unknown>;
  const type = str(e.type);
  if (type === null) return null;

  switch (type) {
    case "chat": {
      const message = str(e.message);
      if (message === null) return null;
      return { kind: "party", name: names.player(num(e.playerId) ?? -1), text: message };
    }
    case "key-found":
      return system(`${names.player(num(e.playerId) ?? -1)} found a key.`);
    case "key-dropped":
      return system(`${names.player(num(e.playerId) ?? -1)} dropped a key.`);
    case "door-unlocked":
      return system(`${names.player(num(e.playerId) ?? -1)} unlocked a door.`);
    case "player-left": {
      const id = num(e.playerId) ?? -1;
      return system(names.isMe(id) ? "You head back to town." : `${names.player(id)} heads back to town.`);
    }
    case "player-respawned":
      return system(`${names.player(num(e.playerId) ?? -1)} is back at the base.`);
    case "combat-death":
      // A hero going down is party business. A goblin going down is not: the
      // bones are on the floor and the kill count is in the party panel.
      return e.targetIsEnemy === true ? null : system(`${names.player(num(e.targetId) ?? -1)} falls.`);
    case "gatestone-generated":
      return system(`${names.player(num(e.playerId) ?? -1)} summoned a gatestone.`);
    case "gatestone-placed":
      return system(`${names.player(num(e.playerId) ?? -1)} dropped a gatestone.`);
    case "gatestone-picked":
      return system(`${names.player(num(e.playerId) ?? -1)} picked up a gatestone.`);
    case "gatestone-teleported":
      return system(`${names.player(num(e.playerId) ?? -1)} teleported to a gatestone.`);
    case "item-dropped": {
      const id = num(e.playerId) ?? -1;
      const kind = num(e.kind);
      const what = kind === null ? null : names.item(str(e.cls) ?? "equipment", kind);
      return system(`${names.player(id)} dropped ${what ?? "an item"} on the ground.`);
    }
    case "invalid": {
      // Feedback for the sender alone (the server filters it), and transient:
      // the ChatPanel fades these out rather than keeping them in the log.
      const reason = str(e.reason);
      return reason === null ? null : { kind: "feedback", text: reason };
    }
    default:
      // moved, arrived, combat-hit, combat-miss, combat-block, vortex, blink,
      // telegrab, inventory-reordered, consumable-used, item-equipped,
      // item-unequipped, drop-picked, sack-withdrawn: the effect layer or the
      // observation already says it.
      return null;
  }
}

const system = (text: string): ChatLine => ({ kind: "system", text });
